import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Send, Loader2, Target, Database, Scale } from "lucide-react";
import { NewsletterSection } from "@/components/NewsletterSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { icon: Database, title: "Researched data", desc: "Every commission rate, cookie duration and payout threshold is researched from official program pages and networks, then reviewed periodically." },
  { icon: Scale, title: "Independent rankings", desc: "Editor's Picks are chosen on EPC, payout reliability and affiliate support — never on how much a program pays us." },
  { icon: Target, title: "Built for earners", desc: "From beginner-friendly badges to ROI calculators, everything is designed to shorten the path to your first (or next) commission." },
];

export default function AboutPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2">About & Contact</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">Honest data for serious affiliates.</h1>
      <p className="text-slate-600 max-w-2xl mb-16 leading-relaxed">
        AffiliateScope exists to answer one question: which programs are actually worth your traffic? We track commissions, EPC benchmarks and payout terms so you don't have to dig through 56 different partner pages.
      </p>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
        {PILLARS.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="bg-white border border-slate-200 rounded-lg p-8">
            <Icon className="w-7 h-7 text-emerald-600 mb-4" strokeWidth={1.8} />
            <h2 className="font-heading text-lg font-semibold mb-2">{title}</h2>
            <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-4">Get in touch</h2>
          <p className="text-slate-600 leading-relaxed mb-6 max-w-md">
            Spotted outdated commission data? Want your program listed? Have a feature idea? Drop us a line — we read everything.
          </p>
          <p className="text-sm text-slate-500 leading-relaxed max-w-md">
            Disclosure: this site may contain affiliate links. We may earn a commission when you join programs through our links, at no cost to you.
          </p>
        </div>
        <form onSubmit={onSubmit} data-testid="contact-form" className="bg-white border border-slate-200 rounded-lg p-8 space-y-5">
          <div>
            <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Name</label>
            <input
              required value={form.name} maxLength={100}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name" data-testid="contact-name-input"
              className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Email</label>
            <input
              type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com" data-testid="contact-email-input"
              className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Message</label>
            <textarea
              required value={form.message} maxLength={5000} rows={5}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="How can we help?" data-testid="contact-message-input"
              className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
            />
          </div>
          <button
            type="submit" disabled={loading} data-testid="contact-submit-button"
            className="flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send message
          </button>
        </form>
      </section>

      <NewsletterSection />
    </main>
  );
}
